<template>
  <div class="pagination-container">
    <div class="pagination-total-records">{{ totalRows }} records</div>
    <div class="pagination-paging-controls">
      <div class="action" :disabled="!hasPrevious() || disableControls" @click="first()">
        <SkipPrevious></SkipPrevious>
      </div>
      &nbsp;
      <div class="action" :disabled="!hasPrevious() || disableControls" @click="previous()">
        <ChevronLeft></ChevronLeft>
      </div>
      <div class="pagination-paging-controls__current">Page {{ currentPage }} of {{ numberOfPages() }}</div>
      <div class="action" :disabled="!hasNext() || disableControls" @click="next()">
        <ChevronRight></ChevronRight>
      </div>
      &nbsp;
      <div class="action" :disabled="!hasNext() || disableControls" @click="last()">
        <SkipNext></SkipNext>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, onMounted } from 'vue';
import ChevronRight from 'vue-material-design-icons/ChevronDoubleRight.vue';
import ChevronLeft from 'vue-material-design-icons/ChevronDoubleLeft.vue';
import SkipNext from 'vue-material-design-icons/SkipNext.vue';
import SkipPrevious from 'vue-material-design-icons/SkipPrevious.vue';

export interface Page {
  start: number;
  end: number;
}

const props = defineProps({
  total: {
    type: Number,
    default: 0,
  },
  rowsPerPage: {
    type: Number,
    default: 50,
  },
  page: {
    type: Number,
    default: 1,
  },
  disableControls: {
    type: Boolean,
    default: false,
  },
});

const currentPage = ref(1);
const totalRows = ref(0);

onMounted(() => {
  currentPage.value = props.page || 1;
  totalRows.value = props.total || 0;
});

watch(
  () => props.total,
  (current, previous) => {
    totalRows.value = current;

    if (totalRows.value < 0) {
      totalRows.value = 0;
    }

    // if new total means that our current page is invalid, then reset back to the first page
    if (currentPage.value > 1 && isPageOutOfBounds(currentPage.value)) {
      currentPage.value = 1;
      pageChanged();
    }
  },
);

function numberOfPages() {
  if (totalRows.value <= 0) {
    return 1;
  }

  return Math.max(Math.ceil(totalRows.value / props.rowsPerPage), 1);
}

function last() {
  if (props.disableControls) {
    return;
  }

  if (hasNext()) {
    currentPage.value = numberOfPages();
    pageChanged();
  }
}

function first() {
  if (props.disableControls) {
    return;
  }

  if (hasPrevious()) {
    currentPage.value = 1;
    pageChanged();
  }
}

function next() {
  if (props.disableControls) {
    return;
  }

  if (hasNext()) {
    currentPage.value++;
    pageChanged();
  }
}

function previous() {
  if (props.disableControls) {
    return;
  }

  if (hasPrevious()) {
    currentPage.value--;
    pageChanged();
  }
}

function hasNext() {
  const next = currentPage.value + 1;
  return !isPageOutOfBounds(next);
}

function hasPrevious() {
  return currentPage.value > 1;
}

function startOfPage(pageNumber?: number): number {
  // default to current page if page number not supplied
  pageNumber = typeof pageNumber !== 'undefined' ? pageNumber : currentPage.value;
  return (pageNumber - 1) * props.rowsPerPage + 1;
}

function endOfPage(pageNumber?: number): number {
  // default to current page if page number not supplied
  pageNumber = typeof pageNumber !== 'undefined' ? pageNumber : currentPage.value;
  return Math.min(pageNumber * props.rowsPerPage, totalRows.value);
}

const emit = defineEmits<{
  (e: 'page-changed', page: Page): void;
}>();

function pageChanged() {
  // our rows are 1-based, but consumers of this will likely have their records stored in 0-based arrays
  emit('page-changed', {
    start: startOfPage() - 1,
    end: endOfPage(),
  });
}

function isPageOutOfBounds(pageNumber: number): boolean {
  return startOfPage(pageNumber) > totalRows.value;
}
</script>

<style scoped lang="scss">
.pagination-container {
  flex: 1;
  display: flex;
  padding: 0.5rem;
  line-height: 1;
}

.pagination-total-records {
  font-size: 0.875rem;
  opacity: 0.75;
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.pagination-paging-controls {
  display: flex;
  flex: 1;
  justify-content: flex-end;
  align-items: center;

  > .action {
    stroke: #000;
    cursor: pointer;
    user-select: none;

    &[disabled] {
      opacity: 0.2;
      cursor: default;
    }
  }
}

.pagination-paging-controls__current {
  font-size: 0.875rem;
  padding: 0 0.5rem;
  opacity: 0.75;
}
</style>
